
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ChatRepositoryInterface,
  ProfileMapInterface,
  ChatReviewFormDataInterface,
  ReviewTagInterface,
} from '@/services/QC-V2/chat/interfaces';
import { CreateTagInterface, TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import { ScoreTypeInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface } from '@/services/QC-V2/score-grade/interfaces';
import ChatComments from '@/components/qa/chat/ChatComments.vue';
import FeedbackDialog, { FeedbackDialogDataInterface } from '@/components/dialogs/qc-v2/feedback/FeedbackDialog.vue';
import {
  ChatFeedbackCreateInterface,
  FeedbackRepositoryInterface,
  FeedbackStatus,
} from '@/services/QC-V2/feedback/interfaces';
import ChatEscalationComments from '@/components/qa/escalation/ChatEscalationComments.vue';
import { ChatEscalationInterface, EscalationRepositoryInterface } from '@/services/QC-V2/escalation/interfaces';

interface ProfilesSelectInterface {
  id: string,
  name: string,
}

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ChatEscalationComments,
    ChatComments,
    FeedbackDialog,
  },
  inject: [
    'chatRepositoryV2',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
    'escalationRepositoryV2',
    'feedbackRepositoryV2',
    'userRepository',
  ],
})
export default class ChatEscalationReview extends Vue {
  @Prop() private escalation!: ChatEscalationInterface;
  @Prop() private scoreTypes!: ScoreTypeInterface[];
  @Prop() private scoreGrades!: ScoreGradeInterface[];
  @Prop() private tags!: TagInterface[];
  @Prop() private profiles!: ProfilesSelectInterface[];
  @Prop() private profilesMap!: ProfileMapInterface;
  @Prop() private disabled!: boolean;

  private selectedUser: string = 'chat';
  private chatRepositoryV2!: ChatRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;
  private escalationRepositoryV2!: EscalationRepositoryInterface;

  private feedbackRepositoryV2!: FeedbackRepositoryInterface;

  private commentProfiles: ProfileMapInterface = {};

  private searchTag: string = '';
  private selectedTags: TagInterface[] = [];

  private formValid: boolean = false;

  private formData: { [userId: string]: ChatReviewFormDataInterface } = {};

  private dataForFeedbackDialog: FeedbackDialogDataInterface | undefined = undefined;
  private feedbackFormValid: boolean = false;
  private feedbackDialogVisible: boolean = false;

  private async created(): Promise<void> {
    await this.initialize();
  }

  @Watch('escalation')
  private async initialize(): Promise<void> {
    this.initializeFormData();
    this.assignChatReviewData();
    this.validateForm();
  }

  private initializeFormData(): void {
    this.selectedUser = 'chat';
    this.searchTag = '';
    this.selectedTags = [];
    this.formData = {
      chat: {
        chatId: this.escalation.chat.id,
        reviewerId: this.$store.getters.id,
        isDraft: false,
        reviewedId: 'chat',
        scores: {},
        tags: [],
        comments: [],
      },
    };

    this.profiles.forEach((profile) => {
      this.formData[profile.id] = {
        chatId: this.escalation.chat.id,
        reviewerId: this.$store.getters.id,
        isDraft: false,
        reviewedId: profile.id,
        scores: {},
        tags: [],
        comments: [],
      };
    });
  }

  private assignChatReviewData(): void {
    this.escalation.chat.tags.forEach((chatTag) => {
      const tag = this.tags.find((t) => t.id === chatTag.tagId);
      if (!tag) {
        return;
      }

      this.formData.chat.tags.push(tag);
    });
  }

  private addScore(scoreTypeId: number, scoreGradeId: number): void {
    this.formData[this.selectedUser].scores[scoreTypeId] = scoreGradeId;
    this.validateForm();
  }

  private removeScore(scoreTypeId: number): void {
    delete this.formData[this.selectedUser].scores[scoreTypeId];
    this.validateForm();
  }

  private async review(): Promise<void> {
    try {
      if (this.formData[this.selectedUser].reviewedId === 'chat') {
        await this.addChatTags();
        return;
      }

      let reviewData: ChatReviewFormDataInterface[] | undefined;
      const formData = Object.values(this.formData)
        .filter((data) => data.reviewedId !== 'chat' && data.scores && Object.values(data.scores).length);
      if (formData.length) {
        reviewData = formData;
      }

      await this.escalationRepositoryV2.reviewChatEscalation(this.escalation.id, reviewData);
      this.addReviews(reviewData);

      this.$successDialog.show('Reviewed successfully');
      this.$emit('review-escalation');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private validateForm(): void {
    if (this.formData[this.selectedUser].reviewedId === 'chat' && this.formData[this.selectedUser].tags.length) {
      this.formValid = true;
      this.feedbackFormValid = false;
      return;
    }

    this.formValid = this.profiles.filter((profile) => profile.id !== 'chat').every((profile) => {
      return Object.values(this.formData[profile.id].scores)
          .filter((score) => !!score).length === this.scoreTypes.length
        || this.escalation.chat.reviews.find((review) => !review.deletedAt && review.reviewedId === profile.id);
    });


    this.feedbackFormValid = !this.escalation.chat.feedbacks
        .find((feedback) => feedback.feedbackTo === this.selectedUser)
      && (Object.values(this.formData[this.selectedUser].scores)
          .filter((score) => !!score).length === this.scoreTypes.length
        || !!this.escalation.chat.reviews
          .find((review) => !review.deletedAt && review.reviewedId === this.selectedUser));

    this.getDataForFeedbackDialog();
    this.$forceUpdate();
  }

  private async addChatTags(): Promise<void> {
    if (this.formData[this.selectedUser].reviewedId !== 'chat') {
      return;
    }

    try {
      await this.tagRepositoryV2.addChatTags(
        this.escalation.chat.id,
        this.formData[this.selectedUser].tags.map((tag) => tag.id),
      );

      this.$successDialog.show('Tags added to chat successfully');
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private async createTag(): Promise<void> {
    if (!this.searchTag) {
      return;
    }

    const data = {
      label: this.searchTag,
      color: '#808080FF',
    } as CreateTagInterface;

    const newTag = await this.tagRepositoryV2.createTag(data);

    this.tags.push(newTag);
    this.formData[this.selectedUser].tags.push(newTag);
  }

  private async createFeedback(): Promise<void> {
    const feedbackData: ChatFeedbackCreateInterface = {
      isDraft: false,
      chatId: this.escalation.chat.id,
      feedbackTo: this.formData[this.selectedUser].reviewedId,
      feedbackFrom: this.$store.getters.id,
      status: FeedbackStatus.SENT,
    };

    try {
      this.formData[this.selectedUser].isDraft = false;

      const existingReview = this.escalation.chat.reviews
        .find((review) => !review.deletedAt && review.reviewedId === this.selectedUser);

      const formIsEmpty = Object.values(this.formData[this.selectedUser].scores)
        .filter((score) => !!score).length !== this.scoreTypes.length;

      if (existingReview && !formIsEmpty) {
        await this.chatRepositoryV2.softDeleteReview(existingReview.id);
      }

      let formData = this.formData[this.selectedUser];
      if (existingReview && formIsEmpty) {
        const scores: { [key: number]: number } = {};
        existingReview.scores.forEach((score) => {
          scores[score.scoreTypeId] = score.scoreGradeId;
        });

        const tags: TagInterface[] = [];
        existingReview.tags.forEach((reviewTag) => {
          const tag = this.tags.find((t) => t.id === reviewTag.tagId);
          if (!tag) {
            return;
          }

          tags.push(tag);
        });

        formData = {
          scores,
          tags,
          isDraft: false,
          reviewedId: existingReview.reviewedId,
          reviewerId: this.$store.getters.id,
          comments: existingReview.comments,
          chatId: this.escalation.chat.id,
        };
      }

      await this.feedbackRepositoryV2.createChatFeedback(feedbackData, formData);
      this.$successDialog.show('Feedback created Successfully');

      this.escalation.chat.feedbacks.push({
        id: 0,
        feedbackTo: formData.reviewedId,
        feedbackFrom: formData.reviewerId,
        timestamp: new Date(),
        status: FeedbackStatus.SENT,
      });

      if (!formIsEmpty) {
        this.escalation.chat.reviews.forEach((review, index) => {
          if (review.reviewedId === formData.reviewedId) {
            this.escalation.chat.reviews[index].deletedAt = new Date().toLocaleString('sv-se');
          }
        });

        this.escalation.chat.reviews.push({
          id: 0,
          reviewedId: formData.reviewedId,
          reviewerId: formData.reviewerId,
          comments: formData.comments.map((c) => ({
            id: 0,
            author: c.author,
            text: c.text,
            rate: c.rate,
            timestamp: c.timestamp,
          })),
          isDraft: formData.isDraft,
          scores: Object.keys(formData.scores).map((scoreTypeId) => ({
            id: 0,
            scoreTypeId: Number(scoreTypeId),
            scoreGradeId: formData.scores[Number(scoreTypeId)],
          })),
          deletedAt: undefined,
          tags: formData.tags.map((tag) => ({ tagId: tag.id })),
          timestamp: new Date(),
        });
      }

      this.formData[this.selectedUser] = {
        chatId: this.escalation.chat.id,
        reviewerId: this.$store.getters.id,
        reviewedId: this.selectedUser,
        scores: {},
        tags: [],
        comments: [],
        isDraft: false,
      };

      this.$emit('feedback-created', this.escalation.chat.reviews);
    } catch (e) {
      this.$errorDialog.show(
        e.response?.data?.message ? e.response.data.message : e.message,
      );
    }
  }

  private getDataForFeedbackDialog(): void {
    if (this.formData[this.selectedUser].reviewedId === 'chat') {
      this.dataForFeedbackDialog = undefined;
      return;
    }

    const user = this.profiles.find((profile) => profile.id === this.selectedUser);

    if (!user) {
      this.dataForFeedbackDialog = undefined;
      return;
    }

    const scores: string[] = [];
    Object.keys(this.formData[this.selectedUser].scores).forEach((scoreTypeId) => {
      const type = this.scoreTypes.find((st) => st.id === Number(scoreTypeId));
      const grade = this.scoreGrades.find(
        (sg) => sg.id === Number(this.formData[this.selectedUser].scores[Number(scoreTypeId)]),
      );

      if (!type || !grade) {
        return;
      }

      scores.push(`${type.label} - ${grade.label}`);
    });

    let comments = this.formData[this.selectedUser].comments;
    let tags = this.formData[this.selectedUser].tags;

    const review = this.escalation.chat.reviews
      .find((r) => !r.deletedAt && r.reviewedId === this.selectedUser);

    if (!scores.length && review) {
      review.scores.forEach((score) => {
        const type = this.scoreTypes.find((st) => st.id === score.scoreTypeId);
        const grade = this.scoreGrades.find((sg) => sg.id === score.scoreGradeId);

        if (!type || !grade) {
          return;
        }

        scores.push(`${type.label} - ${grade.label}`);
      });

      comments = review.comments;

      tags = [];
      review.tags.forEach((tag) => {
        const dbTag = this.tags.find((t) => t.id === tag.tagId);
        if (!tag) {
          return;
        }

        tags.push(dbTag as unknown as ReviewTagInterface);
      });
    }


    this.dataForFeedbackDialog = {
      user,
      scores,
      comments,
      tags,
      usersMap: this.commentProfiles,
    };
  }

  private assignCommentsProfiles(profiles: ProfileMapInterface): void {
    this.commentProfiles = profiles;
    this.getDataForFeedbackDialog();
  }

  private addReviews(reviewsData?: ChatReviewFormDataInterface[]): void {
    if (!reviewsData) {
      return;
    }

    this.escalation.chat.reviews = this.escalation.chat.reviews.map((escalation) => ({
      ...escalation,
      deletedAt: new Date().toLocaleString('sv-se'),
    }));

    reviewsData.forEach((reviewData) => {
      this.escalation.chat.reviews.push({
        id: 0,
        reviewedId: reviewData.reviewedId,
        reviewerId: reviewData.reviewerId,
        comments: reviewData.comments.map((c) => ({
          id: 0,
          author: c.author,
          text: c.text,
          rate: c.rate,
          timestamp: c.timestamp,
        })),
        isDraft: reviewData.isDraft,
        scores: Object.keys(reviewData.scores).map((scoreTypeId) => ({
          id: 0,
          scoreTypeId: Number(scoreTypeId),
          scoreGradeId: reviewData.scores[Number(scoreTypeId)],
        })),
        deletedAt: undefined,
        tags: reviewData.tags.map((tag) => ({ tagId: tag.id })),
        timestamp: new Date(),
      });
    });
  }
}
